import { useMiaChat } from "app/NewAIAvatarLayout/globalContexts/MiaChatContext";
import { PromptType } from "../types";
import { useHistory, useLocation } from "react-router-dom";
import { useEffect, useRef, useCallback, useState } from "react";

// Mapping for homepage prompts to routes
const operationToRouteMap: Record<string, string> = {
  'GENERATE_LEASE_OFFER': '/ai-avatar-layout/generate',
  'GENERATE_NEW_VEHICLE_HIGHLIGHT': '/ai-avatar-layout/generate',
  'GENERATE_USED_VEHICLE_HIGHLIGHT': '/ai-avatar-layout/generate',
  'GENERATE_FINANCE_OFFER': '/ai-avatar-layout/generate',
  'GENERATE_PRIVATE_OFFER': '/ai-avatar-layout/generate',
  'MOVE_DISTRESSED_INVENTORY': '/ai-avatar-layout/target',
  'DATA_ANALYTICS': '/ai-avatar-layout/analyze',
  'TARGET_BUYER_PROFILE': '/ai-avatar-layout/target',
};

const useEntryScreen = (view?: string) => {
    const { segments, operations, latestPrompts } = useMiaChat();
    const history = useHistory();
    const location = useLocation();
    const processedUrlRef = useRef<string>('');
    const lastPathnameRef = useRef<string>('');
    
    // State to track if chat has started (EntryScreen should be hidden)
    const [chatStarted, setChatStarted] = useState(false);
    const [initialMessage, setInitialMessage] = useState<string | undefined>(undefined);

    // Reset processed URL when pathname changes
    useEffect(() => {
        if (lastPathnameRef.current !== location.pathname) {
            processedUrlRef.current = '';
            lastPathnameRef.current = location.pathname;
        }
    }, [location.pathname]);

    const onMessageSend = useCallback((viewParam: string, prompt: PromptType) => {
        try {
            const promptText = prompt.text;
            
            // For generate, analyze, and target views, start chat interface
            if (viewParam === 'generate' || viewParam === 'analyze' || viewParam === 'target') {
                setInitialMessage(promptText);
                setChatStarted(true);
                return;
            }
            
            // For default/home view, navigate based on prompt
            const encodedPrompt = encodeURIComponent(promptText);
            const route = operationToRouteMap[prompt.id];
            if (route) {
                history.push(`${route}?prompt=${encodedPrompt}`);
            } else {
                // Fallback: navigate to home with prompt
                history.push(`/ai-avatar-layout?prompt=${encodedPrompt}`);
            }
        } catch (error) {
            console.error('Error handling message send:', error);
            // On error, still try to start chat for generate/analyze/target
            if (viewParam === 'generate' || viewParam === 'analyze' || viewParam === 'target') {
                setInitialMessage(prompt.text);
                setChatStarted(true);
            }
        }
    }, [history]);

    const homeViewPrompts = latestPrompts.map((prompt) => ({
        text: prompt.prompt,
        id: prompt.operation
    }));

    const analyzeViewPrompts = [
        {
            text: "Analyze campaign performance",
            id: "0"
        },
        {
            text: "Review customer engagement",
            id: "1"
        },
        {
            text: "Identify trends in sales",
            id: "2"
        },
        {
            text: "Evaluate lead quality",
            id: "3"
        }
    ]

    const generateViewPrompts = (): PromptType[] => {
        return operations.map((operation) => ({
            id: operation.id,
            text: operation.display_text
        }));
    }
    
    const targetViewPrompts = (): PromptType[] => {
        return segments.map((segment) => ({
            id: segment._id,
            text: segment.name
        }));
    }

    // Handle prompt query parameter from URL
    useEffect(() => {
        try {
            const currentUrl = `${location.pathname}${location.search}`;
            const searchParams = new URLSearchParams(location.search);
            const promptValue = searchParams.get('prompt');
            
            // Only process if prompt exists, URL has changed, and we have a valid view
            if (promptValue && processedUrlRef.current !== currentUrl && view) {
                const decodedPrompt = decodeURIComponent(promptValue);
                processedUrlRef.current = currentUrl;
                
                // Remove the prompt parameter from URL to avoid re-triggering
                searchParams.delete('prompt');
                const newSearch = searchParams.toString();
                const newUrl = newSearch 
                    ? `${location.pathname}?${newSearch}`
                    : location.pathname;
                history.replace(newUrl);
                
                // Determine which prompts list to use based on view
                let promptsList: PromptType[] = [];
                switch (view) {
                    case 'generate':
                        promptsList = operations.map((operation) => ({
                            id: operation.id,
                            text: operation.display_text
                        }));
                        break;
                    case 'analyze':
                        promptsList = [
                            {
                                text: "Analyzecampaign performance",
                                id: "0"
                            },
                            {
                                text: "Review customer engagement",
                                id: "1"
                            },
                            {
                                text: "Identify trends in sales",
                                id: "2"
                            },
                            {
                                text: "Evaluate lead quality",
                                id: "3"
                            }
                        ];
                        break;
                    case 'target':
                        promptsList = segments.map((segment) => ({
                            id: segment._id,
                            text: segment.name
                        }));
                        break;
                    default:
                        promptsList = latestPrompts.map((prompt) => ({
                            text: prompt.prompt,
                            id: prompt.operation
                        }));
                        break;
                }
                
                // Find the prompt object that matches the text or create a new one
                const prompt = promptsList.find(p => p.text === decodedPrompt) 
                    || promptsList.find(p => p.text.includes(decodedPrompt)) 
                    || { text: decodedPrompt, id: Date.now().toString() };
                
                // Call onMessageSend with the prompt
                onMessageSend(view, prompt);
            }
        } catch (error) {
            console.error('Error processing prompt from URL:', error);
        }
    }, [location.search, location.pathname, view, onMessageSend, operations, segments, latestPrompts, history]);

    return {
        homeViewPrompts,
        analyzeViewPrompts,
        generateViewPrompts,
        targetViewPrompts,
        onMessageSend,
        chatStarted,
        initialMessage,
        resetChat: () => {
            setChatStarted(false);
            setInitialMessage(undefined);
        }
    }
}

export default useEntryScreen;